import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchDashboardOverview } from "../../services/dashboardApi";
import { fetchRevenueStats } from "../../services/analyticsApi";
import { getCached } from "../../utils/adminCache";
import { DEFAULT_DASHBOARD_OVERVIEW, DEFAULT_REVENUE_STATS } from "../../utils/adminStatsDefaults";
import "../../styles/admin-common.css";

const CACHE_KEY = "admin:dashboard:bundle";

const QUICK_LINKS = [
  { to: "/catalog", title: "Catalog hub", desc: "Categories → templates → models" },
  { to: "/orders", title: "Orders", desc: "Search, assign vendors, cancel" },
  { to: "/models", title: "Device Models", desc: "Catalog, pricing, assessment" },
  { to: "/devices", title: "Categories", desc: "Product types & templates" },
  { to: "/questions?scope=category", title: "Question Templates", desc: "Category-level Q&A" },
  { to: "/defects?scope=category", title: "Defect Templates", desc: "Category-level defects" },
  { to: "/regions", title: "Regions", desc: "Service areas & pincodes" },
  { to: "/users", title: "Users", desc: "Customers, vendors, roles" },
  { to: "/analytics", title: "Analytics", desc: "Revenue & performance" },
];

const DashboardHome = () => {
  const [stats, setStats] = useState(DEFAULT_DASHBOARD_OVERVIEW);
  const [revenue, setRevenue] = useState(DEFAULT_REVENUE_STATS);
  const [warning, setWarning] = useState("");
  const [metricsError, setMetricsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async (forceRefresh = false) => {
    const hasCache = !forceRefresh && getCached(CACHE_KEY);
    if (!hasCache) setLoading(true);
    else setRefreshing(true);

    setWarning("");
    setMetricsError(false);
    let partialError = false;

    try {
      const [overview, rev] = await Promise.all([
        fetchDashboardOverview({ forceRefresh }).catch(() => {
          partialError = true;
          return DEFAULT_DASHBOARD_OVERVIEW;
        }),
        fetchRevenueStats({ forceRefresh }).catch(() => {
          partialError = true;
          return DEFAULT_REVENUE_STATS;
        }),
      ]);
      setStats({ ...DEFAULT_DASHBOARD_OVERVIEW, ...overview });
      setRevenue({ ...DEFAULT_REVENUE_STATS, ...rev });
      try {
        sessionStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            data: { overview, rev },
            expiresAt: Date.now() + 5 * 60 * 1000,
          })
        );
      } catch {
        // ignore
      }
      if (partialError) {
        setMetricsError(true);
        setWarning("Some metrics could not be loaded. Refresh to try again.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const cached = getCached(CACHE_KEY);
    if (cached?.overview) {
      setStats({ ...DEFAULT_DASHBOARD_OVERVIEW, ...cached.overview });
      setRevenue({ ...DEFAULT_REVENUE_STATS, ...cached.rev });
      setLoading(false);
    }
    loadData(false);
  }, [loadData]);

  const formatCurrency = (value) =>
    `₹${Number(value ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  if (loading) {
    return <div className="admin-page admin-loading">Loading dashboard</div>;
  }

  const displayCount = (value) => (metricsError ? "—" : (value ?? 0));
  const displayCurrency = (value) => (metricsError ? "—" : formatCurrency(value));

  const cards = [
    { label: "Total Users", value: displayCount(stats.totalUsers) },
    { label: "New Today", value: displayCount(stats.newUsersToday) },
    { label: "Total Orders", value: displayCount(stats.totalOrders) },
    { label: "Pending", value: displayCount(stats.pendingOrders), highlight: !metricsError && (stats.pendingOrders ?? 0) > 0 },
    { label: "Completed Today", value: displayCount(stats.completedOrdersToday) },
    { label: "Vendors", value: displayCount(stats.totalBuyers) },
    { label: "Revenue", value: displayCurrency(stats.totalRevenue) },
    { label: "Vendor Wallets", value: displayCurrency(stats.totalWalletBalance) },
  ];

  return (
    <div className="admin-page">
      <div className="page-header-row">
        <div>
          <h2>Dashboard</h2>
          <p className="subtitle">
            SpotDeal operations at a glance. Metrics refresh every 5 minutes or use Refresh.
          </p>
        </div>
        <button
          type="button"
          className="admin-btn secondary"
          onClick={() => loadData(true)}
          disabled={refreshing}
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {warning && <p className="warning-msg">{warning}</p>}

      <div className="stats-grid">
        {cards.map((card) => (
          <div key={card.label} className="stat-card" style={card.highlight ? { borderColor: "#fbbf24" } : undefined}>
            <span className="stat-label">{card.label}</span>
            <span className="stat-value">{card.value}</span>
          </div>
        ))}
      </div>

      <h3 className="section-heading">Revenue (last 30 days)</h3>
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-label">Vendor top-ups</span>
          <span className="stat-value">{displayCurrency(revenue.creditsLast30Days)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Commission</span>
          <span className="stat-value">{displayCurrency(revenue.debitsLast30Days)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Completed value</span>
          <span className="stat-value">{displayCurrency(revenue.completedValueLast30Days)}</span>
        </div>
      </div>

      {!metricsError && (stats.totalOrders ?? 0) === 0 && (stats.totalUsers ?? 0) <= 1 && (
        <div className="admin-card empty-state-card">
          <h3>Getting started</h3>
          <p>No orders yet. Set up your catalog, add models, and share the app with customers.</p>
          <Link to="/catalog" className="admin-btn">Open catalog hub</Link>
        </div>
      )}

      <div className="admin-card">
        <h3>Quick management</h3>
        <div className="quick-links">
          {QUICK_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className="quick-link-card">
              <strong>{link.title}</strong>
              <span>{link.desc}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
